import React, { useEffect, useState, useRef, useId } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useIdle } from './useIdle';
const sectionLines: Record<string, string> = {
  hero: 'capture: off',
  'how-it-works': 'reading // how_it_works',
  features: 'reading // features',
  privacy: 'reading // privacy',
  'use-cases': 'reading // use_cases',
  faq: 'questions.',
  'final-cta': 'end of page. capture: still off.'
};
export function SessionTicker() {
  const [line, setLine] = useState('');
  const [typed, setTyped] = useState('');
  const [seconds, setSeconds] = useState(0);
  const lastSection = useRef('');
  const idle = useIdle(9000);
  const idleSeen = useRef(false);
  const lastSelectionAt = useRef(0);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // Section narration — route-aware
  useEffect(() => {
    if (pathname === '/pricing') {
      lastSection.current = '';
      setLine('reading // pricing');
      return;
    }
    const ids = ['how-it-works', 'features', 'privacy', 'use-cases', 'faq', 'final-cta'];
    lastSection.current = '';
    setLine(sectionLines.hero);
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target.id !== lastSection.current) {
          lastSection.current = entry.target.id;
          setLine(sectionLines[entry.target.id] ?? '');
        }
      }
    }, {
      rootMargin: '-40% 0px -40% 0px'
    });
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [pathname]);
  // Idle awareness — one line per episode
  useEffect(() => {
    if (idle) {
      idleSeen.current = true;
      setLine('quiet.');
    } else if (idleSeen.current) {
      idleSeen.current = false;
      setLine('back.');
    }
  }, [idle]);
  // Tab-away awareness
  useEffect(() => {
    const originalTitle = document.title;
    const onVisibility = () => {
      if (document.hidden) {
        document.title = '⏸ paused — recall waits for you';
        setLine('away.');
      } else {
        document.title = originalTitle;
        setLine('back.');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.title = originalTitle;
    };
  }, []);
  // Magic word
  useEffect(() => {
    const onMagic = () => setLine('the magic word.');
    window.addEventListener('recall:magic-word', onMagic);
    return () => window.removeEventListener('recall:magic-word', onMagic);
  }, []);
  // Text selection — at most once per 30s
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onSelect = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const sel = window.getSelection()?.toString().trim() ?? '';
        if (sel.length < 4) return;
        const now = Date.now();
        if (now - lastSelectionAt.current < 30000) return;
        lastSelectionAt.current = now;
        setLine('selection noted. not stored.');
      }, 400);
    };
    document.addEventListener('mouseup', onSelect);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', onSelect);
    };
  }, []);
  // Type the line out — slow, with a beat of silence first
  useEffect(() => {
    setTyped('');
    if (!line) return;
    let i = 0;
    let t: ReturnType<typeof setInterval>;
    const start = setTimeout(() => {
      t = setInterval(() => {
        i++;
        setTyped(line.slice(0, i));
        if (i >= line.length) clearInterval(t);
      }, 40);
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval(t);
    };
  }, [line]);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return <div className="font-mono-brand fixed bottom-0 inset-x-0 z-40 hidden items-center gap-3 border-t border-slate-200 bg-white/95 px-4 py-1.5 text-[11px] text-slate-400 backdrop-blur-sm sm:flex" aria-hidden="true" data-id="element-344">
      <span className="text-teal-600/80" data-id="element-345">
        [{mm}:{ss}]
      </span>
      <span className="truncate text-slate-500" data-id="element-346">{typed}</span>
      <span className="caret-blink inline-block h-3 w-[6px] flex-none bg-teal-600/40" data-id="element-347" />
      <span className="ml-auto flex flex-none items-center gap-1.5" data-id="element-348">
        <motion.span className="h-1.5 w-1.5 rounded-full bg-teal-600/70" animate={{
        opacity: idle ? [0.4, 0.15, 0.4] : [0.8, 0.25, 0.8]
      }} transition={{
        duration: idle ? 5 : 3,
        repeat: Infinity
      }} data-id="element-349" />
        demo — nothing is actually recorded
      </span>
    </div>;
}